import _ from 'lodash'
import React from 'react'
import { Classes, Intent, Position, Tooltip } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import classNames from 'classnames'
import './_header.scss'
import './_menu.scss'
import { TetherTarget } from 'browser/components/atomic-elements/atoms/tether-target'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import {
  IMobileHeaderButtonState,
  IMobileHeaderContext,
  MobileHeaderContext,
} from 'browser/mobile/contexts/mobile-header-context'
import { AppNavigatorContext } from 'browser/contexts/app-navigator/app-navigator-context'
import { Menu } from './menu'
import { browserHistory } from 'browser/history'
import apis from 'browser/app/models/apis'
import { PlatformType } from 'shared-libs/models/types/storyboard/storyboard-plan'

const VectorLogo = require('images/onboarding/vector-logo-horizontal.svg')
const languageEnglish = require('images/kiosk/language.english.svg')

const CLICK_TIMEOUT = 10000

interface IHeaderState {
  menuOpen?: boolean
  chatOpen?: boolean
}

export class Header extends React.Component<any, IHeaderState> {
  private clickCount = 0
  private clickTimer: any

  render() {
    return <MobileHeaderContext.Consumer>{this.renderHeader}</MobileHeaderContext.Consumer>
  }

  componentWillUnmount(): void {
    clearTimeout(this.clickTimer)
  }

  private renderHeader = (context: IMobileHeaderContext) => {
    const { enabled, title, chatButton } = context
    const platform = apis.getSettings()?.getPlatform()

    if (!enabled) {
      return null
    }

    if (platform === PlatformType.KIOSK) {
      return this.renderKioskHeader(context)
    }
    return this.renderStandardHeader(context)
  }

  private renderStandardHeader = (context: IMobileHeaderContext) => {
    const { title, chatButton } = context
    return (
      <header className="mobile-header mobile-header--slideDown u-noPrint">
        {this.renderMenuButton()}
        {this.renderExpander()}
        {this.renderTitle(title)}
        {this.renderChatButton(chatButton)}
      </header>
    )
  }

  private renderKioskHeader = (context: IMobileHeaderContext) => {
    const { title, languageButton, kioskHomeButton, kioskHome } = context
    return (
      <header className="mobile-kiosk-header mobile-header--slideDown u-noPrint">
        {this.renderHomeButton(kioskHomeButton, kioskHome)}
        {this.renderExpander()}
        {this.renderTitle(title, 'mobile-kiosk-header')}
        {this.renderLanguageButton(languageButton)}
      </header>
    )
  }

  private renderMenuButton() {
    // TODO: re-enable once VD-10479 (improved new user UX) and VD-10355
    // (improve web auth token handling) are complete. This was originally added
    // in VD-7666, but it lead to a problem where some users were logging out on
    // mweb and couldn't get back into the previous SMS links.
    return null

    const tetherOptions = {
      attachment: 'top right',
      targetAttachment: 'bottom right',
    }
    return (
      <AppNavigatorContext.Consumer>
        {({ settings }) => (
          <TetherTarget
            closeOnPortalClick={true}
            tethered={<Menu settings={settings} />}
            tetherOptions={tetherOptions}
          >
            <Button
              intent={Intent.PRIMARY}
              data-debug-id="headerMenuButton"
              className={classNames('mobile-header__button')}
              icon={IconNames.MENU}
            />
          </TetherTarget>
        )}
      </AppNavigatorContext.Consumer>
    )
  }

  private renderExpander() {
    return <span className="flex-grow" />
  }

  private renderTitle(title: React.ReactNode | null | undefined, className = 'mobile-header') {
    classNames
    let titlePart
    if (title === null) {
      // explicit null value hides the title
      return null
    } else if (title === undefined) {
      titlePart = (
        <span className={this.combineClassNames(className, 'title-container')}>
          <img className={this.combineClassNames(className, 'logo')} src={VectorLogo} />
        </span>
      )
    } else if (_.isString(title)) {
      titlePart = (
        <span className={this.combineClassNames(className, 'title-container')}>
          <Tooltip content={title} position={Position.BOTTOM}>
            <h1 className={this.combineClassNames(className, 'title')}>{title}</h1>
          </Tooltip>
        </span>
      )
    } else {
      titlePart = (
        <span className={this.combineClassNames(className, 'title-container')}>
          <h1 className={this.combineClassNames(className, 'title')}>{title}</h1>
        </span>
      )
    }
    return (
      <Button className={`${Classes.getClassNamespace()}-minimal`} onClick={this.countClicks}>
        {titlePart}
      </Button>
    )
  }

  private combineClassNames = (block, element) => {
    return `${block}__${element}`
  }

  private countClicks = () => {
    this.clickCount++
    if (this.clickCount === 10) {
      console.log('open remote config editor')
      browserHistory.push('/remote-config?isMobile=true')
      clearTimeout(this.clickTimer)
      this.clickTimer = null
    }
    if (!this.clickTimer) {
      this.clickTimer = setTimeout(() => {
        console.log('timed out waiting for clicks')
        this.clearClickCount()
        this.clickTimer = null
      }, CLICK_TIMEOUT)
    }
  }

  private clearClickCount = _.debounce(() => {
    this.clickCount = 0
  }, CLICK_TIMEOUT)

  private renderChatButton(button: IMobileHeaderButtonState | null | undefined) {
    if (_.isNil(button)) {
      // any nil value hides the chat button
      return null
    }
    const className = button?.className
    return (
      <Button
        intent={Intent.PRIMARY}
        data-debug-id="headerChatButton"
        icon={IconNames.CHAT}
        className={classNames('mobile-header__button', className)}
        onClick={button?.handler}
      />
    )
  }

  private renderLanguageButton(button: IMobileHeaderButtonState | null | undefined) {
    if (_.isNil(button)) {
      // any nil value hides the chat button
      return null
    }
    const className = button?.className
    return (
      <Button
        intent={Intent.NONE}
        data-debug-id="headerLanguageButton"
        className={classNames('mobile-kiosk-header__button', className)}
        onClick={button?.handler}
      >
        <img
          className={this.combineClassNames('mobile-kiosk-header', 'button')}
          src={languageEnglish}
        />
      </Button>
    )
  }

  private renderHomeButton(button: IMobileHeaderButtonState | null | undefined, kioskHome?: string) {
    if (_.isNil(button) && _.isEmpty(kioskHome)) {
      // any nil value hides the chat button
      return null
    }
    const className = button?.className
    const defaultClickHandler = () => {
      browserHistory.push(kioskHome ?? '/kiosk-search')
    }
    return (
      <Button
        intent={Intent.NONE}
        data-debug-id="headerHomeButton"
        className={classNames(className, 'mobile-kiosk_header__button')}
        onClick={button?.handler ?? defaultClickHandler}
        size="200px"
        icon={IconNames.HOME}
      />
    )
  }
}
